import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import { useIsMobile } from '../hooks/useIsMobile';

// Bounds mirror the server-side validation in handlers/settings.ts so the user
// gets immediate feedback; the backend remains the source of truth.
const AGING_MIN = 30;
const AGING_MAX = 730;
const OVERDUE_MIN = 60;
const OVERDUE_MAX = 1095;

const SettingsPage: React.FC = () => {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const { refresh } = useSettings();

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn:  api.getSettings,
  });

  const [aging, setAging]       = useState('');
  const [overdue, setOverdue]   = useState('');
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState(false);

  // Prefill inputs once the current values load.
  useEffect(() => {
    if (data) {
      setAging(data.verification_aging_days ?? '');
      setOverdue(data.verification_overdue_days ?? '');
    }
  }, [data]);

  const validate = (): string | null => {
    if (!/^\d+$/.test(aging) || !/^\d+$/.test(overdue)) {
      return 'Both thresholds must be whole numbers.';
    }
    const a = parseInt(aging, 10);
    const o = parseInt(overdue, 10);
    if (a < AGING_MIN || a > AGING_MAX) {
      return `Aging threshold must be between ${AGING_MIN} and ${AGING_MAX} days.`;
    }
    if (o < OVERDUE_MIN || o > OVERDUE_MAX) {
      return `Overdue threshold must be between ${OVERDUE_MIN} and ${OVERDUE_MAX} days.`;
    }
    if (a >= o) {
      return 'The aging threshold must be less than the overdue threshold.';
    }
    return null;
  };

  const mutation = useMutation({
    mutationFn: () =>
      api.updateSettings({
        verification_aging_days:   parseInt(aging, 10),
        verification_overdue_days: parseInt(overdue, 10),
      }),
    onSuccess: () => {
      setSuccess(true);
      setError('');
      // Refresh app-wide settings so badges/labels/filters update immediately.
      qc.invalidateQueries({ queryKey: ['settings'] });
      qc.invalidateQueries({ queryKey: ['kpi'] });
      refresh();
      setTimeout(() => setSuccess(false), 4000);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error ?? 'Failed to save settings.');
      setSuccess(false);
    },
  });

  const handleSave = () => {
    const v = validate();
    if (v) { setError(v); setSuccess(false); return; }
    setError('');
    mutation.mutate();
  };

  const dirty = data
    ? aging !== (data.verification_aging_days ?? '') || overdue !== (data.verification_overdue_days ?? '')
    : false;

  const inputStyle: React.CSSProperties = {
    width: isMobile ? '100%' : 120,
    padding: '8px 12px',
    border: '1px solid var(--gray-300)',
    borderRadius: 6,
    fontSize: 14,
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>Settings</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 14 }}>
          Verification thresholds used across the directory, dashboards, and contact badges.
        </p>
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--gray-500)' }}>Loading…</p>
      ) : (
        <div className="card" style={{ padding: 24 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Verification Thresholds</h2>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, margin: '12px 0 20px',
            padding: '10px 12px', background: 'var(--hazard-50)', border: '1px solid var(--hazard-50)',
            borderRadius: 6, fontSize: 12.5, color: 'var(--hazard-deep-700)',
          }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Changing these values retroactively reclassifies <strong>all</strong> contacts.
              A contact's status (Verified / Aging / Overdue) is computed live from these
              thresholds — there is no per-contact migration.
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 4 : 16 }}>
              <label style={{ fontSize: 14, fontWeight: 600, minWidth: 200 }}>
                Aging after (days)
              </label>
              <input
                type="number" min={AGING_MIN} max={AGING_MAX} value={aging}
                onChange={(e) => { setAging(e.target.value); setSuccess(false); }}
                style={inputStyle}
              />
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                Verified within this window. ({AGING_MIN}–{AGING_MAX})
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 4 : 16 }}>
              <label style={{ fontSize: 14, fontWeight: 600, minWidth: 200 }}>
                Overdue after (days)
              </label>
              <input
                type="number" min={OVERDUE_MIN} max={OVERDUE_MAX} value={overdue}
                onChange={(e) => { setOverdue(e.target.value); setSuccess(false); }}
                style={inputStyle}
              />
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                Must exceed aging. ({OVERDUE_MIN}–{OVERDUE_MAX})
              </span>
            </div>
          </div>

          {error && (
            <p style={{ color: 'var(--red)', fontSize: 14, marginTop: 16 }}>{error}</p>
          )}
          {success && (
            <p style={{ color: 'var(--green)', fontSize: 14, marginTop: 16 }}>Settings saved.</p>
          )}

          <div style={{ marginTop: 24 }}>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={mutation.isPending || !dirty}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Save size={14} /> {mutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
